export const SPARK_TEST_GROUPS = [
  "civilian-spark-jw-test",
  "civilian-spark-ll-test",
  "civilian-spark-nd-test",
  "civilian-spark-pm-test",
  "civilian-spark-rl-test",
  "civilian-spark-zr-test",
];

export const USER_MANAGEMENT_HELP = {
  title: "User Management Help",
  eyebrow: "Administration",
  summary:
    "Create test accounts in your assigned Spark test group.",
  sections: [
    {
      heading: "Add one user",
      steps: [
        "Click Add User +.",
        "Pick your assigned Group.",
        "Enter email and password.",
        "Select User for a normal test account.",
        "Check Admin too if you need admin pages or backend logs.",
        "Click Submit.",
      ],
    },
    {
      heading: "Spark test groups",
      body:
        "Use your own group so test sessions, summaries, reminders, and model changes stay isolated.",
      tags: SPARK_TEST_GROUPS,
    },
    {
      heading: "Defaults to check",
      bullets: [
        "Automate summarization: keep on for realistic session tests.",
        "SMS/email reminders: enable only when testing reminder delivery.",
        "Send onboarding email: turn off for silent local test accounts.",
        "Disable user: creates an inactive account.",
      ],
    },
    {
      heading: "Table actions",
      bullets: [
        "Edit: change group, roles, reminders, phone, or disabled status.",
        "Delete: permanently removes the account after email confirmation.",
      ],
    },
    {
      heading: "Gotchas",
      bullets: [
        "Emails must be unique. Archive the old account before reusing one.",
        "Phone numbers must be +1 plus 10 digits.",
      ],
      callout:
        "Phone confirmed applies when editing users. New user creation currently does not send that field.",
    },
  ],
  screenshots: [
    {
      src: "",
      alt: "User Management toolbar with Add User highlighted",
      caption: "Toolbar entry point for single-user creation.",
    },
    {
      src: "",
      alt: "Add User form with group, credentials, and user type highlighted",
      caption: "Required fields for a normal test account.",
    },
  ],
};

export const MODEL_CONFIGURATION_HELP = {
  title: "Model Configuration Help",
  eyebrow: "Configuration",
  summary:
    "Use this panel to load an existing model version, edit prompt JSON, save a new version, and optionally activate it for one group and one model role.",
  sections: [
    {
      heading: "Normal prompt-testing workflow",
      steps: [
        "Pick your assigned test group.",
        "Pick the model role you want to test, usually core for the main coaching behavior.",
        "Pick the current active version and click Load.",
        "Edit only the prompt content you intend to test.",
        "Use Format and the built-in JSON validation before saving.",
        "Review the diff, then Save as new version.",
        "Keep Activate after save checked only when you want new sessions in that group to use the version immediately.",
      ],
    },
    {
      heading: "Switch active versions",
      steps: [
        "Pick any older or newer version in Version.",
        "Click Activate selected to make it active without editing it.",
        "Use the same button again on another version to roll forward or roll back.",
      ],
      callout:
        "Activate selected changes the live version. Load only changes what is shown in the editor.",
    },
    {
      heading: "What model role means",
      bullets: [
        "core generates the main coaching reply.",
        "self_help runs the safety/self-harm check before the core response.",
        "end_detection decides whether the conversation is ready to close.",
        "tgrow_summary, soap_summary, and birp_summary create post-session summaries.",
        "sms_message and email_message generate reminder messages.",
      ],
    },
    {
      heading: "Core prompt pieces",
      promptMap: {
        label: "Core prompt structure diagram",
        title: "Core prompt structure",
        start: {
          label: "Session starts",
          chips: ["welcome", "welcome_back"],
        },
        branches: [
          {
            condition: "No prior summary",
            prompt: "session_without_summary",
            detail: "wraps first_session",
          },
          {
            condition: "Prior summary exists",
            prompt: "session_with_summary",
            detail: "wraps first_session plus memory context",
          },
        ],
        context: {
          label: "Runtime context injected",
          chips: [
            "prev_summary",
            "older_summaries",
            "older_notes",
            "adaptive_context",
            "current date",
          ],
        },
        base: {
          label: "Base coaching prompt",
          chips: ["first_session"],
        },
        output: "Final core system prompt sent to the model",
        optional: [
          "voice_style prepended for voice output",
          "hate / jailbreak / sexual fallback replies",
        ],
      },
      bullets: [
        "first_session is the base coaching persona, tone, boundaries, and domain framing.",
        "session_without_summary wraps first_session for a user without prior summary context.",
        "session_with_summary wraps first_session and receives previous summaries or notes.",
        "welcome is the first assistant message for a new user.",
        "welcome_back is appended when the backend is creating a returning-session opener.",
        "voice_style controls text-to-speech style when voice output is used.",
        "hate, jailbreak, and sexual are fallback replies for provider content filters.",
      ],
    },
    {
      heading: "How prompt components are assembled",
      bullets: [
        "The backend chooses session_with_summary when a previous summary exists; otherwise it uses session_without_summary.",
        "Each prompt's content_variables list tells the backend which {{variable}} placeholders to replace.",
        "Runtime context such as previous summary, older summaries, older notes, adaptive context, and today's date is injected by the backend.",
        "Saving creates a new row named like core_civilian-spark-pm-test_version_002. Existing versions are not overwritten.",
        "Activating core, self_help, or end_detection closes active chat connections for that group so clients reconnect on the new model.",
      ],
    },
    {
      heading: "What to edit first",
      bullets: [
        "For most behavior tests, start with prompts.first_session.content in the core role.",
        "For memory and returning-user behavior, inspect session_with_summary and welcome_back.",
        "For closing behavior, edit core closing instructions first; edit end_detection only if the detector is ending too early or too late.",
        "For summary quality, edit the system and user prompts in the specific summary role you are evaluating.",
      ],
    },
  ],
  screenshots: [
    {
      src: "",
      alt: "Model Configuration selectors for group, model role, family, and version",
      caption: "Load a version for one group and one model role before editing.",
    },
    {
      src: "",
      alt: "Prompt JSON editor and diff review",
      caption: "Validate JSON and review changes before saving a new version.",
    },
  ],
};
